import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useState } from 'react';
import { z } from 'zod';

import { type UserType, mutateUserDelete, mutateUserUpdate } from '@/api/user';
import { useAppForm } from '@/components/Form';
import { Panel } from '@/components/panels';
import { FEATURE_FLAG } from '@/constants/featureFlags';
import { PermissionGroup } from '@/features/PermissionGroup';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import useInstanceNavigate from '@/hooks/useInstanceNavigate';
import { useNotification } from '@/providers/Notification';
import { ROUTES } from '@/router';

import UserDeleteDialog from './UserDeleteDialog';
import UserDetailHeader from './UserDetailHeader';
import UserDetailInfoFields from './UserDetailInfoFields';

type Props = {
  user: UserType;
  onSubmitSuccess: (newUser: UserType) => void;
};

const UserDetailContent = ({ user, onSubmitSuccess }: Props) => {
  const { pushNotification, removeNotification } = useNotification();
  const isAIDigestNextEnabled = useFeatureFlag(FEATURE_FLAG.AI_DIGEST_NEXT) === true;
  const navigate = useInstanceNavigate();
  const [isEditing, setEditing] = useState(false);
  const [isSaving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const formSchema = z.object({
    name: z
      .string()
      .trim()
      .min(1, t`Name is required`),
    email: z.string(),
    role: z.enum(['user', 'admin'] as const),
    digestDelivery: z.object({
      repeatsWeeks: z.number(),
    }),
  });

  const form = useAppForm({
    defaultValues: user as Omit<UserType, 'id'>,
    validators: {
      onSubmit: formSchema,
      onSubmitAsync: async ({ value }) => {
        setSaving(true);

        try {
          const { name, role, digestDelivery } = value;

          await mutateUserUpdate({ id: user.id, name, role, digestDelivery });

          onSubmitSuccess({
            ...user,
            ...value,
          });

          pushNotification({ type: 'success', message: t`Success! User has been saved.` });
          removeNotification({ type: 'error' });

          setEditing(false);
          form.reset(value, { keepDefaultValues: true });
        } catch {
          pushNotification({
            type: 'error',
            message: t`Apologies, but something went wrong. Please try to Save again.`,
          });
        } finally {
          setSaving(false);
        }
      },
    },
  });

  const handleDelete = async () => {
    try {
      const { success } = await mutateUserDelete(user.id);
      if (!success) {
        throw new Error();
      }

      navigate(ROUTES.users.path);
    } catch {
      pushNotification({
        type: 'error',
        message: t`Apologies, but something went wrong. Please try to Delete again.`,
      });
    } finally {
      setShowDeleteModal(false);
    }
  };

  const handleEditChange = (isEditing: boolean) => {
    if (!isEditing) {
      form.reset(user, { keepDefaultValues: true });
    }
    setEditing(isEditing);
  };

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        event.stopPropagation();
        void form.handleSubmit();
      }}
    >
      <form.AppForm>
        <div className="flex w-full flex-col gap-6">
          <UserDetailHeader
            isDeletable
            isEditable={user.status === 'active'}
            isEditing={isEditing}
            isSaving={isSaving}
            onEditChange={handleEditChange}
            onDelete={() => setShowDeleteModal(true)}
          />
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-24">
            <div className="lg:col-span-24 xl:col-span-9 2xl:col-span-7">
              <Panel title={<Trans>User Details</Trans>} style={{ height: '100%' }}>
                <UserDetailInfoFields
                  userId={user.id}
                  isEmailEditable={false}
                  isDigestDeliveryEditable={!isAIDigestNextEnabled}
                  isEditing={isEditing}
                  isSaving={isSaving}
                />
              </Panel>
            </div>
            <div className="lg:col-span-24 xl:col-span-15 2xl:col-span-17">
              <Panel title={<Trans>Permission Setting</Trans>} style={{ height: '100%' }}>
                <form.AppField name="role">
                  {(field) => (
                    <field.Field>
                      <PermissionGroup
                        isEditing={isEditing}
                        isSaving={isSaving}
                        value={field.state.value}
                        onValueChange={field.handleChange}
                        onBlur={field.handleBlur}
                      />
                      <field.Error />
                    </field.Field>
                  )}
                </form.AppField>
              </Panel>
            </div>
          </div>
        </div>
      </form.AppForm>
      <UserDeleteDialog
        name={user?.name}
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onDelete={handleDelete}
      />
    </form>
  );
};

export default UserDetailContent;
